/**
 * Shared contracts between the Express backend and the React frontend.
 *
 * The dashboard is built around a single unit — the "pane" (Danish: rude) — which
 * always carries a {@link SourceDescriptor} so the UI (and generated docs) can show
 * the customer exactly where each number comes from.
 */

export type Department = 'sales' | 'marketing' | 'support' | 'admin';

export const DEPARTMENTS: readonly Department[] = ['sales', 'marketing', 'support', 'admin'];

export type SourceSystem =
  | 'superoffice'
  | 'google-analytics'
  | 'activecampaign'
  | 'computed';

/**
 * How a pane's value was resolved:
 * - `live`  — connector configured and the query succeeded with data.
 * - `empty` — connector configured and the query succeeded but returned no data.
 * - `stale` — connector failed; serving the last-known-good cached value.
 * - `error` — connector unavailable/unconfigured and no cache (production).
 * - `mock`  — development/test fixture fallback (never produced in production).
 */
export type DataMode = 'live' | 'mock' | 'stale' | 'empty' | 'error';

/** Documents exactly where a pane's data comes from. Surfaced in the UI + docs. */
export interface SourceDescriptor {
  system: SourceSystem;
  /** Short label, e.g. "SuperOffice · sagskategori Sales/Leads/MQL". */
  label: string;
  /** Precise detail: category path, agent/endpoint, GA metric, AC list, etc. */
  detail: string;
  /** Optional documentation/deep link for the source. */
  docUrl?: string;
  /** Whether this value is live or mock right now. */
  mode: DataMode;
  /** Assumptions / open questions the customer must confirm. */
  caveats?: string[];
  /** When `mode === 'error'`, a short, secret-free Danish reason for the UI. */
  errorReason?: string;
  /** ISO timestamp of the last successful live value (drives the `stale` display). */
  lastLiveAt?: string;
}

/** Period selector for a pane query — a named token or an explicit ISO date range. */
export type PanePeriodToken = 'thisMonth' | 'lastMonth' | 'nextMonth' | 'thisQuarter' | 'lastQuarter' | 'thisYear' | 'lastYear';
export type PanePeriod = PanePeriodToken | { from: string; to: string };

/**
 * Typed query params for a pane: which period to show + optional filters. Carried from
 * the client into every pane builder so charts, metrics, drill rows and the cache key
 * all reflect the same selection. Filters that a pane does not support are ignored.
 */
export interface PaneQuery {
  period?: PanePeriod;
  /** Owner / salesperson filter (SuperOffice associate), where the source supports it. */
  owner?: string;
  /** Customer / company filter, where the source supports it. */
  customer?: string;
  /** ActiveCampaign list filter, where the source supports it. */
  listId?: string;
}

export type MetricFormat = 'number' | 'currency' | 'percent' | 'duration';

export interface TrendPoint {
  label: string;
  value: number;
}

export interface MetricValue {
  value: number;
  format: MetricFormat;
  /** Optional unit suffix, e.g. "sessioner". */
  unit?: string;
  /** Delta vs previous comparable period as a ratio (0.042 = +4.2%). */
  deltaPct?: number | null;
  /** Optional per-period trend (e.g. last 6 months). */
  trend?: TrendPoint[];
  /** Optional max for gauge/ring rendering (e.g. a rating out of 5). */
  max?: number;
}

export interface ProgressValue {
  current: number;
  target: number;
  format: MetricFormat;
  unit?: string;
}

export type PaneKind = 'metric' | 'list' | 'ranking' | 'progress' | 'agenda';

/** One underlying record behind a pane's number — shown in the drill-down modal. */
export interface DetailRow {
  id: string;
  title: string;
  subtitle?: string;
  value?: string;
  meta?: string;
  /** Deep link to the specific record in the source system. */
  href?: string;
  /** Rich HTML content (e.g. an appointment body) rendered in the drill modal. */
  html?: string;
  /** Period/category this row belongs to — used to filter when a datapoint is clicked. */
  bucket?: string;
  /** Avatar/photo for the row (e.g. an employee leaderboard) — same-origin proxy URL. */
  imageUrl?: string;
}

/** One slice of a categorical breakdown (for pie/donut charts). */
export interface BreakdownSlice {
  label: string;
  value: number;
}

/** One series in a stacked monthly trend (e.g. one ActiveCampaign list). */
export interface StackSeries {
  /** Stable id (e.g. the list id). */
  id: string;
  /** Display name (e.g. the list name). */
  name: string;
  /** One value per month, positionally aligned with StackedTrend.months. */
  values: number[];
}

/** A multi-series, stacked monthly trend (per-series growth + stacked total). */
export interface StackedTrend {
  /** Month labels, oldest first. */
  months: string[];
  /** One entry per series (stack order). */
  series: StackSeries[];
  /** Stacked total per month (sum across series), months-aligned. */
  total: number[];
}

/** A cumulative actual-vs-target comparison (e.g. personal won vs personal budget). */
export interface ComparisonTrend {
  /** What is being compared, e.g. "Din udvikling mod mål". */
  label: string;
  /** Month labels, oldest first. */
  months: string[];
  /** Cumulative actual per month — only up to the current month (shorter than months). */
  actual: number[];
  /** Cumulative target per month, months-aligned. */
  target: number[];
}

/** How a table cell renders. `currency`/`number`/`percent` right-align and format da-DK. */
export type TableCellFormat = 'text' | 'currency' | 'number' | 'percent' | 'date';

export interface TableColumn {
  key: string;
  label: string;
  format?: TableCellFormat;
}

export interface TableRow {
  id: string;
  /** Cell values by column key — numbers stay numeric so totals/formatting are exact. */
  cells: Record<string, string | number | null>;
  /** Deep link to the record in the source system. */
  href?: string;
  /** Group value (e.g. sale type / ticket category) for adaptive grouping. */
  group?: string;
  /** In-dashboard drill target (e.g. a ticket's message thread). */
  drill?: { kind: 'ticket'; id: number };
  /** The record is closed/afsluttet — the drill table can hide these on demand (they
   *  always stay in the pane's counts; the toggle only filters the table view). */
  closed?: boolean;
}

/** A rich entity table for the drill-down modal (sales, tickets, …). */
export interface PaneTable {
  columns: TableColumn[];
  rows: TableRow[];
  /** Column keys summed into a footer row. MUST NOT include the first column (it carries the total label). */
  totals?: string[];
  /** Label for the group dimension (e.g. "Salgstype"); enables the group toggle. */
  groupLabel?: string;
}

/** A fully-resolved pane ready to render. */
export interface PaneData {
  id: string;
  department: Department;
  title: string;
  kind: PaneKind;
  source: SourceDescriptor;
  metric?: MetricValue;
  rows?: DetailRow[];
  progress?: ProgressValue;
  note?: string;
  /** Categorical breakdown for a pie/donut (optional). */
  breakdown?: BreakdownSlice[];
  /** Multi-series stacked monthly trend (e.g. per-list subscriber growth). */
  stacked?: StackedTrend;
  /** The underlying records behind this pane, for the drill-down modal. */
  details?: DetailRow[];
  /** Cumulative actual-vs-target comparison, rendered in the drill-down modal. */
  comparison?: ComparisonTrend;
  /** Rich entity table for the drill-down modal — preferred over `details` when present. */
  table?: PaneTable;
  /**
   * Human label for the window the numbers actually cover (e.g. "Denne måned",
   * "Sidste uge", "13.07.2026 – 31.07.2026"). Always reflects the RESOLVED period —
   * when the user changes the period selector, this changes with it.
   */
  periodLabel?: string;
  updatedAt: string;
}

export interface ConnectorStatus {
  system: SourceSystem;
  label: string;
  mode: DataMode;
  detail?: string;
}

export interface AuthUser {
  name: string;
  email: string;
  roles: string[];
  /**
   * Immutable principal id ("tid:oid" from the verified Entra ID token; a synthetic
   * stable value in dev-bypass). Emails are mutable/reassignable and must NOT key
   * per-user data — this is what per-user cache scoping uses.
   */
  subject: string;
}

export interface AuthState {
  authenticated: boolean;
  /** True when running with the dev auth bypass (Entra ID not configured). */
  devBypass: boolean;
  user: AuthUser | null;
}

/** Danish display titles per department. */
export const DEPARTMENT_TITLES: Record<Department, string> = {
  sales: 'Salg',
  marketing: 'Marketing',
  support: 'Support',
  admin: 'Ledelse',
};
